import {
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { Subject, Subscription } from 'rxjs';
import { AbstractControl, FormControl, FormGroup } from '@angular/forms';
import { CommonService } from '../../../../services/common.service';
import { Store } from '@ngrx/store';
import { takeUntil } from 'rxjs/operators';
import { MatSelect } from '@angular/material/select';

@Component({
    selector: 'app-dropdown',
    templateUrl: './dropdown.component.html',
    styleUrls: ['../../styles.scss', './dropdown.component.scss'],
    standalone: false
})
export class DropdownComponent implements OnInit, OnChanges, OnDestroy {
  @ViewChild('select') input: ElementRef | undefined;
  @Input() loading = false;
  @Input() label = '';
  /** A status pill after the label (e.g. "Autofilled", Figma Status badge 35281:21972); empty = none. */
  @Input() labelBadge = '';
  @Input() options: { value: any; label: string; disabled?: boolean; children?: any[] }[] = [];
  @Input() enableClearSelection = false;
  @Input() parentFormGroup: FormGroup = new FormGroup({});
  @Input() controlName = '';
  @Input() error = '';
  @Input() placeholder = '';
  @Input() focus = false;
  @Input() enableMultiple = false;
  @Input() showCount = false;
  @Input() selectionString = 'selected';
  @Input() optionsPanelClassName = '';
  @Input() disableAllOptions = false;
  @Input() defaultValue: any;
  @Input() enableSearch = false;
  @Input() enableAddItem = false;
  @Input() closeOnEverySelect = false;
  @Input() enableDetailedOptionView = false;
  @Input() enableRegistryDetailedOptionView = false;
  @Input() enableOthers = false;
  @Input() enableAddNew = false;
  @Input() addNewLabel = 'Add new';
  @Input() sentenceCase = false;
  @Input() hint = '';
  /**
   * Opt-in: options panel width, passed to mat-select's panelWidth. 'auto' (default) = the
   * field's width; a number = that many px (e.g. 280 so a half-width field's list isn't cramped).
   */
  @Input() panelWidth: string | number | null = 'auto';
  @Output() checkForErrors: EventEmitter<any> = new EventEmitter();
  // Emits the current search text so callers can prefill the new item.
  @Output() addNewClicked: EventEmitter<string> = new EventEmitter();
  /** Emits whenever the options panel closes — lets a parent act on a finished multi-selection. */
  @Output() panelClosed: EventEmitter<void> = new EventEmitter();
  /** Emits the option created by addItem() so a parent can keep custom options in shared state. */
  @Output() itemAdded: EventEmitter<{ value: any; label: string }> = new EventEmitter();
  fieldSubscription: Subscription | undefined;
  isRequired = false;
  isDisabled = false;
  optionsDisabled = false;
  searchForm = new FormGroup({
    text: new FormControl(''),
  });
  filteredOptions = [];
  addText = '';
  disabledClass: boolean;
  @ViewChild('select') private select: MatSelect;
  private readonly onDestroy: Subject<any> = new Subject<any>();

  constructor(private commonService: CommonService, private store: Store, private cdRef: ChangeDetectorRef) {}

  /**
   * String-equality comparison so a form value of (e.g.) `728` matches an
   * <mat-option [value]="'728'">. Without compareWith, mat-select uses
   * identity comparison which leaves the select empty whenever the seed
   * value's primitive type differs from the option's value type. Safe to
   * apply globally — the dropdown options are always string/number/null
   * primitives, never reference-typed objects.
   */
  compareWithValue = (a: any, b: any): boolean => {
    if (a === b) return true;
    if (a === undefined || a === null || b === undefined || b === null) return false;
    return String(a) === String(b);
  };

  // Do not show field as touched and having error when clicked outside the field
  @HostListener('focusout')
  onBlur() {
    if (!this.error) {
      setTimeout(() => {
        this.parentFormGroup?.controls[this.controlName].markAsUntouched();
      }, 150);
    }
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes.options) {
      // A parent may bind a lookup that has no entry yet (e.g. a per-feature
      // options map before it's populated); treat that as no options.
      this.options = this.options ?? [];
      this.filteredOptions = [...this.options];

      if (this.enableAddNew) {
        const addNewExists = this.filteredOptions.some(opt => opt.value === 'ADD_NEW');
        if (!addNewExists) {
          this.filteredOptions.unshift({ label: '+ Add new', value: 'ADD_NEW' });
        }
      }

      if (this.enableOthers) {
        const othersExists = this.filteredOptions.some(opt => opt.value === '_others');
        if (!othersExists) {
          this.filteredOptions.push({ label: 'Others', value: '_others' });
        }
      }

      this.filterOptions();
    }
  }

  ngOnInit(): void {
    const control = this.parentFormGroup.controls[this.controlName];
    this.isDisabled = control?.status === 'DISABLED';

    this.options = this.options ?? [];
    this.filteredOptions = [...this.options];
    if (this.enableAddNew) {
      const addNewExists = this.filteredOptions.some(opt => opt.value === 'ADD_NEW');
      if (!addNewExists) {
        this.filteredOptions.unshift({ label: '+ Add new', value: 'ADD_NEW' });
      }
    }
    if (this.enableOthers) {
      const othersExists = this.filteredOptions.some(opt => opt.value === '_others');
      if (!othersExists) {
        this.filteredOptions.push({ label: 'Others', value: '_others' });
      }
    }

    this.fieldSubscription = control?.valueChanges.subscribe(value => {
      if (this.closeOnEverySelect) {
        this.select.close();
      }

      if (this.enableMultiple) {
        if (value?.includes(-1)) {
          this.optionsDisabled = true;
          this.parentFormGroup.controls[this.controlName].patchValue([-1], { emitEvent: false });
        } else if (value?.includes(-2)) {
          const selectedValues = [];
          this.options?.forEach(option => {
            selectedValues.push(option.value);
          });
          if (selectedValues.includes(-1)) {
            const index = selectedValues.indexOf(-1);
            selectedValues.splice(index, 1);
          }
          this.parentFormGroup.controls[this.controlName].patchValue([...selectedValues], { emitEvent: false });
          this.optionsDisabled = false;
        } else {
          this.optionsDisabled = false;
        }
      }

      // If field is marked as touched, mark it as untouched
      if (this.parentFormGroup?.controls[this.controlName]?.touched) {
        this.parentFormGroup?.controls[this.controlName].markAsUntouched();
      }

      // Clear errors and re-validate so stale errors don't persist after value change
      this.parentFormGroup.controls[this.controlName].setErrors(null);
      this.checkForErrors.emit(this.controlName);
      this.parentFormGroup.controls[this.controlName].updateValueAndValidity({ emitEvent: false });
    });

    // Check if field is required
    if (control?.validator) {
      const validator = control.validator({} as AbstractControl);
      this.isRequired = !!(validator && validator['required']);
    }

    this.searchForm.controls.text.valueChanges.pipe(takeUntil(this.onDestroy)).subscribe(value => {
      this.addText = value;
      const exists = this.options.some(option => option?.label === value);
      this.disabledClass = !!exists; // Set disabled class based on match
      this.filterOptions();
    });
  }

  clearSelection($event: Event) {
    $event.stopPropagation();
    this.parentFormGroup.controls[this.controlName].setValue(this.defaultValue || '');
    this.parentFormGroup.markAsDirty();
  }

  filterOptions() {
    const searchText = this.searchForm.controls.text.value?.toLowerCase() || '';
    if (searchText) {
      // In multi-select mode keep already-selected options rendered: mat-select
      // rebuilds its selection from the rendered <mat-option>s, so filtering a
      // selected option out of the DOM silently un-selects it.
      const controlValue = this.parentFormGroup?.controls[this.controlName]?.value;
      const selected: any[] = this.enableMultiple && Array.isArray(controlValue) ? controlValue : [];
      this.filteredOptions = [...this.options].filter(o =>
        o.label.toLowerCase().includes(searchText) || selected.includes(o.value),
      );

      if (this.enableAddNew) {
        const addNewExists = this.filteredOptions.some(opt => opt.value === 'ADD_NEW');
        if (!addNewExists) {
          this.filteredOptions.unshift({ label: '+ Add new', value: 'ADD_NEW' });
        }
      }

      if (this.enableOthers) {
        const othersExists = this.filteredOptions.some(opt => opt.value === '_others');
        if (!othersExists) {
          this.filteredOptions.push({ label: 'Others', value: '_others' });
        }
      }
      this.cdRef.detectChanges();
    } else {
      this.filteredOptions = [...this.options];
      if (this.enableAddNew) {
        const addNewExists = this.filteredOptions.some(opt => opt.value === 'ADD_NEW');
        if (!addNewExists) {
          this.filteredOptions.unshift({ label: '+ Add new', value: 'ADD_NEW' });
        }
      }
      if (this.enableOthers) {
        const othersExists = this.filteredOptions.some(opt => opt.value === '_others');
        if (!othersExists) {
          this.filteredOptions.push({ label: 'Others', value: '_others' });
        }
      }
    }
  }

  addItem() {
    const text = this.searchForm.controls.text.value;
    if (!text) {
      return;
    }
    const value = text.toLowerCase();
    const option = { value, label: text };
    this.options = [option, ...this.options];
    this.itemAdded.emit(option);

    const control = this.parentFormGroup.controls[this.controlName];
    if (this.enableMultiple) {
      const current = Array.isArray(control.value) ? control.value : [];
      control.setValue([...current, value]);
    } else {
      control.setValue(value);
    }
    this.searchForm.controls.text.setValue('');
    this.select.close();
  }

  onAddNewClick(event: Event) {
    event.stopPropagation();
    this.select.close();
    this.addNewClicked.emit(this.searchForm.controls.text.value?.trim() || '');
  }

  getLabelForSelectedOption(): string {
    const controlValue = this.parentFormGroup?.get(this.controlName)?.value;
    if (!controlValue) return '';
    const selectedOption = this.filteredOptions.find(o => o.value === controlValue);
    return selectedOption ? selectedOption.label : '';
  }

  get hasTagOptions(): boolean {
    return this.options.some(o => o['tag']);
  }

  getHeadingForSelectedOption(): string {
    const controlValue = this.parentFormGroup?.get(this.controlName)?.value;
    if (!controlValue) {
      return '';
    }

    if (Array.isArray(controlValue)) {
      return controlValue
        .map(val => this.filteredOptions.find(o => o.id === val)?.heading)
        .filter(Boolean)
        .join(', ');
    }

    // Look in all options so the label survives a search that filters the selection out.
    const selectedOption = this.options.find((o: any) => o.id === controlValue);
    if (!selectedOption) {
      return '';
    }
    return [selectedOption['heading'], selectedOption['title']].filter(Boolean).join(' · ');
  }

  onOpenedChange(opened: boolean) {
    if (!opened) {
      this.panelClosed.emit();
    }
  }

  ngOnDestroy() {
    this.fieldSubscription?.unsubscribe();
    this.onDestroy.next(null);
    this.onDestroy.complete();
  }
}
